import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getApiErrorMessage, getApiFieldErrors } from "../../lib/apiError";
import {
  useModeratorsAuthForgotPasswordCreate,
  useModeratorsAuthResetPasswordCreate,
} from "../../services/moderators/generated/moderators-auth/moderators-auth";
import { EMAIL_PATTERN, RESET_CODE_TTL_MINUTES } from "./ForgetPasswordPage";
import "./AuthPage.css";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

type FieldErrors = {
  email?: string;
  otp?: string;
  password?: string;
  passwordConfirm?: string;
};

/** Keep digits only (so pasting "123 456" or "123-456" works) and cap at 6. */
const sanitizeCode = (value: string) => value.replace(/\D/g, "").slice(0, CODE_LENGTH);

/**
 * Moderator reset, step 2: email + the 6-digit code from the email + new
 * password → `POST /api/v1/moderators/auth/resetPassword/`
 * `{ email, otp, newPassword }`. Success ends all of the moderator's sessions.
 */
const ModeratorResetPasswordPage = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail =
    typeof (location.state as { email?: unknown } | null)?.email === "string"
      ? (location.state as { email: string }).email
      : "";

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const resetPasswordMutation = useModeratorsAuthResetPasswordCreate({
    mutation: {
      onSuccess: () => {
        toast.success(t("resetPassword.toastSuccess"));
        navigate("/moderator/login", { replace: true });
      },
      onError: (error) => {
        const fieldErrors = getApiFieldErrors(error);
        const next: FieldErrors = {
          email: fieldErrors.email,
          otp: fieldErrors.otp,
          password: fieldErrors.newPassword,
        };
        setErrors(next);
        if (!next.email && !next.otp && !next.password) {
          setSubmitError(getApiErrorMessage(error, t("resetPassword.errors.failed")));
        }
      },
    },
  });

  const resendMutation = useModeratorsAuthForgotPasswordCreate({
    mutation: {
      onSuccess: () => {
        setCode("");
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(t("moderatorResetPassword.toastResent"));
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, t("resetPassword.errors.resendFailed")));
      },
    },
  });

  const validateEmail = (value: string): string | undefined => {
    if (!value) return t("shared.errors.enterEmail");
    if (!EMAIL_PATTERN.test(value)) return t("shared.errors.validEmail");
    return undefined;
  };

  const handleResend = () => {
    setSubmitError(null);
    const trimmed = email.trim();
    const emailError = validateEmail(trimmed);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }
    setErrors((prev) => ({ ...prev, email: undefined, otp: undefined }));
    resendMutation.mutate({ data: { email: trimmed } });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const trimmedEmail = email.trim();
    const nextErrors: FieldErrors = {};

    nextErrors.email = validateEmail(trimmedEmail);
    if (code.length !== CODE_LENGTH) {
      nextErrors.otp = t("resetPassword.errors.enterCode");
    }
    if (!password) {
      nextErrors.password = t("resetPassword.errors.enterNewPassword");
    } else if (password.length < 8) {
      nextErrors.password = t("resetPassword.errors.passwordTooShort");
    }
    if (!passwordConfirm) {
      nextErrors.passwordConfirm = t("resetPassword.errors.confirmPassword");
    } else if (password !== passwordConfirm) {
      nextErrors.passwordConfirm = t("resetPassword.errors.passwordsMismatch");
    }

    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    resetPasswordMutation.mutate({
      data: { email: trimmedEmail, otp: code, newPassword: password },
    });
  };

  return (
    <div className="auth-page">
      <div className="auth-page__background" />

      <div className="container">
        <div className="auth-page__inner">
          <button type="button" className="auth-page__back-link" onClick={() => navigate("/moderator/login")}>
            {t("shared.backToLogin")}
          </button>

          <section className="auth-card" aria-label={t("moderatorResetPassword.ariaLabel")}>
            <div className="auth-card__icon-wrapper">
              <div className="auth-card__icon auth-card__icon--primary">
                <span className="auth-card__icon-glyph" aria-hidden="true">
                  <i className="fa-solid fa-user-shield" />
                </span>
              </div>
            </div>

            <header className="auth-card__header">
              <h1 className="auth-card__title">{t("moderatorResetPassword.title")}</h1>
              <p className="auth-card__subtitle">
                {t("moderatorResetPassword.subtitle", { minutes: RESET_CODE_TTL_MINUTES })}
              </p>
            </header>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="moderator-reset-email">
                  {t("shared.emailLabel")}
                </label>
                <div className={`auth-form__input-wrapper ${errors.email ? "auth-form__input-wrapper--error" : ""}`}>
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-regular fa-envelope" />
                  </span>
                  <input
                    id="moderator-reset-email"
                    type="email"
                    className="auth-form__input"
                    placeholder={t("shared.moderatorEmailPlaceholder")}
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
                {errors.email ? <p className="auth-form__error">{errors.email}</p> : null}
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="moderator-reset-code">
                  {t("resetPassword.codeLabel")}
                </label>
                <div className={`auth-form__input-wrapper ${errors.otp ? "auth-form__input-wrapper--error" : ""}`}>
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-solid fa-hashtag" />
                  </span>
                  <input
                    id="moderator-reset-code"
                    type="text"
                    className="auth-form__input"
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    value={code}
                    onChange={(event) => setCode(sanitizeCode(event.target.value))}
                    style={{ letterSpacing: "0.3em" }}
                    required
                  />
                </div>
                {errors.otp ? <p className="auth-form__error">{errors.otp}</p> : null}
                <p className="auth-form__hint">
                  {t("resetPassword.didntGetIt")}{" "}
                  <button
                    type="button"
                    className="auth-form__link"
                    onClick={handleResend}
                    disabled={resendMutation.isPending || resendCooldown > 0}
                  >
                    {resendMutation.isPending
                      ? t("common:actions.sending")
                      : resendCooldown > 0
                        ? t("resetPassword.resendCodeCooldown", { seconds: resendCooldown })
                        : t("resetPassword.resendCode")}
                  </button>
                </p>
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="moderator-reset-password">
                  {t("resetPassword.newPasswordLabel")}
                </label>
                <div
                  className={`auth-form__input-wrapper auth-form__input-wrapper--password ${
                    errors.password ? "auth-form__input-wrapper--error" : ""
                  }`}
                >
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-solid fa-lock" />
                  </span>
                  <input
                    id="moderator-reset-password"
                    type={showPassword ? "text" : "password"}
                    className="auth-form__input"
                    placeholder={t("resetPassword.newPasswordPlaceholder")}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="auth-form__password-toggle"
                    aria-label={showPassword ? t("shared.hidePassword") : t("shared.showPassword")}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    <i className={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} />
                  </button>
                </div>
                {errors.password ? <p className="auth-form__error">{errors.password}</p> : null}
              </div>

              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="moderator-reset-password-confirm">
                  {t("resetPassword.confirmPasswordLabel")}
                </label>
                <div
                  className={`auth-form__input-wrapper auth-form__input-wrapper--password ${
                    errors.passwordConfirm ? "auth-form__input-wrapper--error" : ""
                  }`}
                >
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-solid fa-lock" />
                  </span>
                  <input
                    id="moderator-reset-password-confirm"
                    type={showConfirmPassword ? "text" : "password"}
                    className="auth-form__input"
                    placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(event) => setPasswordConfirm(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="auth-form__password-toggle"
                    aria-label={
                      showConfirmPassword
                        ? t("resetPassword.hideConfirmPassword")
                        : t("resetPassword.showConfirmPassword")
                    }
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    <i className={showConfirmPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} />
                  </button>
                </div>
                {errors.passwordConfirm ? <p className="auth-form__error">{errors.passwordConfirm}</p> : null}
              </div>

              {submitError && (
                <p className="auth-form__error auth-form__error--block" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                className="auth-form__submit auth-form__submit--primary"
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? t("resetPassword.submitting") : t("resetPassword.submit")}
              </button>

              <p className="auth-form__footer-text">
                {t("shared.rememberPassword")}{" "}
                <button type="button" className="auth-form__link" onClick={() => navigate("/moderator/login")}>
                  {t("shared.signInLink")}
                </button>
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ModeratorResetPasswordPage;
